import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight, Sparkles, Repeat } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

// PayPal redirects here with ?subscription_id=I-XXXX after buyer approval.
// PayPal marks the subscription ACTIVE asynchronously — we poll for up to ~20s.
const MAX_POLLS = 10;
const POLL_INTERVAL_MS = 2000;

export default function SubscriptionSuccess() {
  const [params] = useSearchParams();
  const subId = params.get("subscription_id");
  const [state, setState] = useState({ status: "loading", data: null, error: null });
  const pollCount = useRef(0);
  const { user } = useAuth();

  const pending = (() => {
    try { return JSON.parse(sessionStorage.getItem("dpa_pending_checkout") || "null"); } catch { return null; }
  })();

  useEffect(() => {
    if (!subId) {
      setState({ status: "error", error: "Missing subscription id", data: null });
      return;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/paypal/subscriptions/${subId}`);
        if (cancelled) return;
        if (data.status === "ACTIVE" || data.paypal_status === "ACTIVE") {
          setState({ status: "success", data, error: null });
          sessionStorage.removeItem("dpa_pending_checkout");
          return;
        }
        pollCount.current += 1;
        if (pollCount.current >= MAX_POLLS) {
          setState({ status: "pending", data, error: null });
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        if (cancelled) return;
        setState({ status: "error", data: null, error: e?.response?.data?.detail || e.message || "Could not confirm subscription" });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [subId]);

  const label = pending?.label || (state.data && `${state.data.sku}`) || "your subscription";
  const nextBill = state.data?.next_billing_time || state.data?.paypal_next_billing_time;

  return (
    <div className="container-narrow py-16 md:py-24" data-testid="subscription-success-page">
      <div className="max-w-xl mx-auto card-soft p-8 md:p-10 text-center">
        {state.status === "loading" && (
          <>
            <Loader2 className="w-12 h-12 text-marigold animate-spin mx-auto mb-4" />
            <h1 className="font-heading text-2xl md:text-3xl font-semibold mb-2">Activating your subscription…</h1>
            <p className="text-ink2 text-sm">PayPal is confirming the recurring authorization. This usually takes a few seconds.</p>
          </>
        )}

        {state.status === "success" && (
          <>
            <div className="w-16 h-16 rounded-full bg-teal/15 text-teal flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="w-9 h-9" />
            </div>
            <div className="label-eyebrow mb-2 inline-flex items-center gap-2"><Sparkles className="w-3.5 h-3.5" /> Subscription active</div>
            <h1 className="font-heading text-3xl md:text-4xl font-semibold mb-2">Welcome aboard!</h1>
            <p className="text-ink2 mb-6">You&apos;re now subscribed to <span className="font-semibold text-ink">{label}</span>. It will renew automatically until you cancel.</p>

            <div className="bg-paper rounded-xl border border-border px-4 py-3 mb-6 text-left text-sm space-y-1" data-testid="subscription-summary">
              <div className="flex justify-between"><span className="text-ink2">Tier</span><span className="font-heading font-semibold capitalize">{state.data.tier}</span></div>
              <div className="flex justify-between"><span className="text-ink2">Billing</span><span className="font-heading font-semibold capitalize inline-flex items-center gap-1"><Repeat className="w-3.5 h-3.5" /> {state.data.interval_unit === "YEAR" ? "Yearly" : "Monthly"}</span></div>
              {nextBill && (
                <div className="flex justify-between"><span className="text-ink2">Next renewal</span><span className="font-heading font-semibold">{new Date(nextBill).toLocaleDateString()}</span></div>
              )}
              <div className="flex justify-between"><span className="text-ink2">Subscription ID</span><span className="font-mono text-xs">{state.data.paypal_subscription_id}</span></div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to={user ? "/dashboard" : "/"} className="btn-primary" data-testid="go-dashboard-btn">
                Go to dashboard <ArrowRight className="w-3.5 h-3.5" />
              </Link>
              <Link to="/studio" className="btn-outline-ink text-sm" data-testid="go-studio-btn">
                Start building
              </Link>
            </div>
          </>
        )}

        {state.status === "pending" && (
          <>
            <Loader2 className="w-12 h-12 text-marigold animate-spin mx-auto mb-4" />
            <h1 className="font-heading text-2xl md:text-3xl font-semibold mb-2">Almost there…</h1>
            <p className="text-ink2 text-sm mb-6">Your PayPal subscription is approved but not yet marked ACTIVE. Refresh in a moment or head to your dashboard — we&apos;ll activate it as soon as PayPal notifies us.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button onClick={() => window.location.reload()} className="btn-primary" data-testid="refresh-btn">
                Refresh <ArrowRight className="w-3.5 h-3.5" />
              </button>
              <Link to="/dashboard" className="btn-outline-ink text-sm">Go to dashboard</Link>
            </div>
          </>
        )}

        {state.status === "error" && (
          <>
            <div className="w-16 h-16 rounded-full bg-red-100 text-red-600 flex items-center justify-center mx-auto mb-5">
              <XCircle className="w-9 h-9" />
            </div>
            <h1 className="font-heading text-2xl md:text-3xl font-semibold mb-2">We couldn&apos;t confirm the subscription</h1>
            <p className="text-ink2 mb-6 text-sm">{state.error}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/pricing" className="btn-primary" data-testid="retry-checkout-btn">
                Back to pricing <ArrowRight className="w-3.5 h-3.5" />
              </Link>
              <a href="mailto:hello@durgaprogrammingadventures.com" className="btn-outline-ink text-sm">
                Contact support
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
